import { ComponentType, lazy } from 'react';

const SESSION_STORAGE_KEY = 'chunk_reload_attempted';

/** Safe sessionStorage getter — returns null if storage is unavailable. */
const getReloadFlag = (): boolean => {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

/** Safe sessionStorage setter — silently fails if storage is unavailable. */
const setReloadFlag = (): void => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
  } catch {
    // Private browsing or disabled storage — proceed without loop guard
  }
};

/** Safe sessionStorage remover — silently fails if storage is unavailable. */
const clearReloadFlag = (): void => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Ignore — storage unavailable
  }
};

/**
 * Wraps React.lazy with automatic retry on chunk load failures.
 *
 * When a deploy replaces hashed JS chunks, users with stale manifests
 * will request old chunk URLs that no longer exist. Amplify's SPA fallback
 * serves index.html (with 200) for those missing files, causing a
 * SyntaxError when the browser tries to parse HTML as JavaScript.
 *
 * This wrapper catches such failures and triggers a single hard reload
 * to fetch the latest manifest. A sessionStorage flag prevents infinite
 * reload loops.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyWithRetry = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> =>
  lazy(async () => {
    try {
      const module = await importFn();
      clearReloadFlag();
      return module;
    } catch (error) {
      if (!getReloadFlag()) {
        setReloadFlag();
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }

      throw error;
    }
  });
